'use client'

import {
  ensembleContactStep,
  ensembleSignupSchema,
  gigOfferInput,
  lineupTypeLabels,
  lineupTypes,
  PRICE_FLOOR_EURO,
} from '@ensemble.music/core'
import { Button, Checkbox, FormField, field, html, inputValue } from '@ensemble.music/ui'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import type { z } from 'zod'
import { useStoredState } from '../wizard/useStoredState'
import { StepBlock, WizardShell } from '../wizard/WizardShell'

const steps = ['Kontakt', 'Gig eintragen', 'Weitere Infos & abschließen'] as const
// the step lives in the URL, so back/forward in the browser move between steps
const slugs = ['kontakt', 'gig', 'abschliessen'] as const

const emptyForm = {
  ensembleName: '',
  contactName: '',
  email: '',
  phone: '',
  region: '',
  gigDateFrom: '',
  gigDateTo: '',
  gigPrice: '',
  gigNote: '',
  skipGig: false,
  lineupType: '',
  genres: '',
  priceFromEuro: '',
  videoUrl: '',
  websiteUrl: '',
  consent: false,
  website: '',
}
type FormState = typeof emptyForm
type Errors = Record<string, string>

// v1 in the key: if the shape ever changes incompatibly, bump it instead of migrating drafts
const STORAGE_KEY = 'ensemble:ensemble-signup:v1'

// gig fields are flat in the form and nested in the schema
const gigField: Record<string, keyof FormState> = {
  dateFrom: 'gigDateFrom',
  dateTo: 'gigDateTo',
  priceFromEuro: 'gigPrice',
  note: 'gigNote',
}

/** `gig.dateTo` (schema, server) -> `gigDateTo` (form field). */
const formKey = (key: string) => (key.startsWith('gig.') ? (gigField[key.slice(4)] ?? key) : key)

const contactKeys = Object.keys(ensembleContactStep.shape)

/** Which step a field (or a server error key like `gig.dateTo`) belongs to. */
function stepOf(key: string) {
  if (contactKeys.includes(key)) return 0
  if (key.startsWith('gig')) return 1
  return 2
}

function toErrors(error: z.ZodError, rename: (key: string) => string = (key) => key): Errors {
  const out: Errors = {}
  for (const issue of error.issues) {
    const key = rename(issue.path.join('.') || '_')
    if (!out[key]) out[key] = issue.message
  }
  return out
}

const gigCandidate = (form: FormState) => ({
  dateFrom: form.gigDateFrom,
  dateTo: form.gigDateTo,
  priceFromEuro: form.gigPrice,
  note: form.gigNote,
})

const gigEntered = (form: FormState) => !form.skipGig && form.gigDateFrom !== ''

const payload = (form: FormState) => ({
  ensembleName: form.ensembleName,
  contactName: form.contactName,
  email: form.email,
  phone: form.phone,
  region: form.region,
  lineupType: form.lineupType || undefined,
  genres: form.genres,
  priceFromEuro: form.priceFromEuro,
  videoUrl: form.videoUrl,
  websiteUrl: form.websiteUrl,
  consent: form.consent,
  website: form.website,
  gig: gigEntered(form) ? gigCandidate(form) : undefined,
})

/** The first step whose data is incomplete - a deep link or a refresh never skips past it. */
function firstOpenStep(form: FormState) {
  if (!ensembleContactStep.safeParse(form).success) return 0
  if (!form.skipGig && !gigOfferInput.safeParse(gigCandidate(form)).success) return 1
  return 2
}

export function EnsembleSignupScreen() {
  const router = useRouter()
  const params = useSearchParams()
  const index = Math.max(0, slugs.indexOf(params.get('schritt') as (typeof slugs)[number]))
  const [form, setForm, ready, clear] = useStoredState(STORAGE_KEY, emptyForm)
  const [errors, setErrors] = useState<Errors>({})
  const [sending, setSending] = useState(false)
  const [direction, setDirection] = useState<1 | -1>(1)

  const go = (next: number, replace = false) => {
    setDirection(next > index ? 1 : -1)
    const url = `/fuer-ensembles/anmelden?schritt=${slugs[next]}`
    if (replace) router.replace(url, { scroll: false })
    else router.push(url, { scroll: false })
  }

  // once the draft is loaded, send a visitor who jumped ahead back to the step that is missing
  useEffect(() => {
    if (!ready) return
    const open = firstOpenStep(form)
    if (index > open) go(open, true)
  }, [ready, index])

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((previous) => ({ ...previous, [key]: value }))
    // a message about the old value is wrong the moment the value changes
    setErrors(({ [key]: _, ...rest }) => rest)
  }

  function next() {
    if (index === 0) {
      const parsed = ensembleContactStep.safeParse(form)
      if (!parsed.success) return setErrors(toErrors(parsed.error))
    }
    if (index === 1) {
      const parsed = gigOfferInput.safeParse(gigCandidate(form))
      if (!parsed.success) return setErrors(toErrors(parsed.error, (key) => gigField[key] ?? key))
      set('skipGig', false)
    }
    setErrors({})
    go(index + 1)
  }

  function skipGig() {
    setErrors({})
    set('skipGig', true)
    go(2)
  }

  // an error on an earlier step is invisible here, so take the visitor to it
  function showErrors(next: Errors) {
    setErrors(next)
    const earliest = Math.min(
      ...Object.keys(next).map((key) => (key === '_' ? index : stepOf(key)))
    )
    if (earliest < index) go(earliest)
  }

  async function submit() {
    const parsed = ensembleSignupSchema.safeParse(payload(form))
    if (!parsed.success) {
      return showErrors(toErrors(parsed.error, formKey))
    }

    setErrors({})
    setSending(true)
    try {
      const response = await fetch('/api/v1/ensembles', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(parsed.data),
      })
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { errors?: Errors } | null
        const serverErrors: Errors = {}
        for (const [key, message] of Object.entries(body?.errors ?? {})) {
          serverErrors[formKey(key)] = message
        }
        if (Object.keys(serverErrors).length === 0) {
          serverErrors._ = 'Das hat nicht funktioniert. Bitte versuche es noch einmal.'
        }
        return showErrors(serverErrors)
      }
      clear()
      router.push('/fuer-ensembles/danke')
    } catch {
      setErrors({ _: 'Keine Verbindung zum Server. Bitte prüfe deine Internetverbindung.' })
    } finally {
      setSending(false)
    }
  }

  const text = (
    key: keyof FormState,
    label: string,
    options: {
      hint?: string
      required?: boolean
      type?: string
      autoComplete?: string
      inputMode?: 'numeric'
    } = {}
  ) => (
    <FormField
      id={key}
      label={label}
      hint={options.hint}
      error={errors[key]}
      required={options.required}
    >
      <html.input
        className={field(!!errors[key])}
        id={key}
        name={key}
        type={options.type ?? 'text'}
        autoComplete={options.autoComplete}
        inputMode={options.inputMode}
        aria-invalid={errors[key] ? 'true' : undefined}
        aria-describedby={errors[key] ? `${key}-error` : options.hint ? `${key}-hint` : undefined}
        value={String(form[key])}
        onChange={(event) => set(key, inputValue(event) as never)}
      />
    </FormField>
  )

  return (
    <WizardShell
      homeHref="/fuer-ensembles"
      steps={steps}
      current={index}
      direction={direction}
    >
      <html.form
        className="flex flex-col gap-8"
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          if (index < 2) next()
          else void submit()
        }}
      >
        {errors._ ? (
          <html.div
            role="alert"
            className="bg-danger-soft border border-border-color-danger rounded-xl p-3"
          >
            <html.p className="text-base text-on-danger-soft">{errors._}</html.p>
          </html.div>
        ) : null}

        {/* Fields render only once the draft is loaded, so a refresh never flashes empty inputs. */}
        {ready && index === 0 ? (
          <StepBlock index={0}>
            <html.p className="text-lg text-color-secondary text-center">
              Wer seid ihr, und wie erreichen wir euch?
            </html.p>
            {text('ensembleName', 'Ensemblename', { required: true, autoComplete: 'organization' })}
            {text('contactName', 'Dein Name', { required: true, autoComplete: 'name' })}
            {text('email', 'E-Mail', {
              required: true,
              type: 'email',
              autoComplete: 'email',
              hint: 'Hierher schicken wir passende Anfragen.',
            })}
            {text('phone', 'Telefon', {
              type: 'tel',
              autoComplete: 'tel',
              hint: 'Optional, für kurze Rückfragen.',
            })}
            {text('region', 'Region', {
              required: true,
              hint: 'Wo seid ihr zu Hause? Zum Beispiel Stuttgart oder Ludwigsburg.',
            })}
          </StepBlock>
        ) : null}

        {ready && index === 1 ? (
          <StepBlock index={0}>
            <html.p className="text-lg text-color-secondary text-center">
              Wann habt ihr Zeit, und ab welchem Preis spielt ihr? Für genau diesen Zeitraum
              schlagen wir euch vor.
            </html.p>
            <html.div className="flex flex-row flex-wrap gap-4">
              <html.div className="flex flex-col grow shrink basis-[200px]">
                {text('gigDateFrom', 'Frei ab', { required: true, type: 'date' })}
              </html.div>
              <html.div className="flex flex-col grow shrink basis-[200px]">
                {text('gigDateTo', 'Frei bis', { required: true, type: 'date' })}
              </html.div>
            </html.div>
            {text('gigPrice', 'Preis ab (Euro)', {
              required: true,
              inputMode: 'numeric',
              hint: `Mindestens ${PRICE_FLOOR_EURO} €.`,
            })}
            <FormField
              id="gigNote"
              label="Notiz"
              hint="Optional. Zum Beispiel: nur abends, maximal 50 km Anfahrt."
              error={errors.gigNote}
            >
              <html.textarea
                className={field(false, true)}
                id="gigNote"
                rows={3}
                value={form.gigNote}
                onChange={(event) => set('gigNote', inputValue(event))}
              />
            </FormField>
          </StepBlock>
        ) : null}

        {ready && index === 2 ? (
          <StepBlock index={0}>
            <html.p className="text-lg text-color-secondary text-center">
              Noch ein paar Angaben zu eurer Musik, dann seid ihr dabei.
            </html.p>
            <FormField
              id="lineupType"
              label="Besetzung"
              error={errors.lineupType}
              required
            >
              <html.div
                role="radiogroup"
                aria-label="Besetzung"
                className="flex flex-row flex-wrap gap-2"
              >
                {lineupTypes.map((type) => (
                  <Button
                    key={type}
                    type="button"
                    role="radio"
                    aria-checked={form.lineupType === type}
                    tone={form.lineupType === type ? 'primary' : 'secondary'}
                    onClick={() => set('lineupType', type)}
                  >
                    {lineupTypeLabels[type]}
                  </Button>
                ))}
              </html.div>
            </FormField>
            {text('genres', 'Genres', {
              hint: 'Optional, mit Komma getrennt. Zum Beispiel Pop, Soul, Jazz.',
            })}
            {text('priceFromEuro', 'Gage ab (Euro)', {
              required: true,
              inputMode: 'numeric',
              hint: `Euer Einstiegspreis für einen Auftritt, mindestens ${PRICE_FLOOR_EURO} €.`,
            })}
            {text('videoUrl', 'Video-Link', {
              type: 'url',
              hint: 'YouTube, Vimeo oder eure Website. Wir schauen es uns vor der Freigabe an.',
            })}
            {text('websiteUrl', 'Website', { type: 'url', hint: 'Optional.' })}

            {/* honeypot: off-screen for people, just another field for bots */}
            <html.div
              aria-hidden="true"
              className="absolute left-[-9999px] opacity-0"
            >
              <html.label htmlFor="website">Website</html.label>
              <html.input
                id="website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(event) => set('website', inputValue(event))}
              />
            </html.div>

            <html.div className="flex flex-row items-start gap-3">
              <Checkbox
                id="consent"
                checked={form.consent}
                onCheckedChange={(checked) => set('consent', checked === true)}
                invalid={!!errors.consent}
                aria-invalid={errors.consent ? 'true' : undefined}
                aria-describedby={errors.consent ? 'consent-error' : undefined}
              />
              <html.div className="flex flex-col gap-1">
                <html.label
                  htmlFor="consent"
                  className="text-sm text-color-secondary"
                >
                  Ich bin damit einverstanden, dass ensemble unsere Angaben speichert und sie
                  Bookern zeigt, wenn wir uns für ihre Anfrage vorschlagen. Details in der{' '}
                  <html.a
                    href="/datenschutz"
                    target="_blank"
                    className="text-link hover:underline"
                    rel="noopener"
                  >
                    Datenschutzerklärung
                  </html.a>
                  . *
                </html.label>
                {errors.consent ? (
                  <html.p
                    id="consent-error"
                    role="alert"
                    className="text-sm text-danger"
                  >
                    {errors.consent}
                  </html.p>
                ) : null}
              </html.div>
            </html.div>
          </StepBlock>
        ) : null}

        {ready ? (
          <StepBlock index={1}>
            <html.div className="flex flex-row flex-wrap-reverse items-center justify-between gap-3">
              {index > 0 ? (
                <Button
                  type="button"
                  tone="ghost"
                  onClick={() => {
                    setErrors({})
                    go(index - 1)
                  }}
                >
                  Zurück
                </Button>
              ) : (
                <html.span />
              )}
              <html.div className="flex flex-row flex-wrap items-center gap-3">
                {index === 1 ? (
                  <Button
                    type="button"
                    tone="secondary"
                    onClick={skipGig}
                  >
                    Ohne Gig weiter
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  size="lg"
                  disabled={sending}
                  aria-busy={sending}
                >
                  {index < 2 ? 'Weiter' : sending ? 'Wird gesendet …' : 'Anmeldung abschicken'}
                </Button>
              </html.div>
            </html.div>
            <html.p className="text-sm text-color-tertiary text-center">
              Eure Angaben bleiben in diesem Browser gespeichert, bis ihr abschickt.
            </html.p>
          </StepBlock>
        ) : null}
      </html.form>
    </WizardShell>
  )
}
